import React, { useEffect, useRef, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useTranslation } from 'react-i18next';
import { EmailQueueItem, EmailQueueService } from '../services/EmailQueueService';

const EmailQueueList: React.FC = () => {
    const { t } = useTranslation();
    const toast = useRef<Toast>(null);
    const [items, setItems] = useState<EmailQueueItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [retrying, setRetrying] = useState<string | null>(null);
    const [retryingAll, setRetryingAll] = useState(false);
    const [viewItem, setViewItem] = useState<EmailQueueItem | null>(null);

    const fetchQueue = async () => {
        setLoading(true);
        try {
            const data = await EmailQueueService.fetchQueue();
            setItems(data);
        } catch (error: any) {
            toast.current?.show({ severity: 'error', summary: t('emailQueue.fetchError'), detail: error.message, life: 3000 });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchQueue();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleRetry = async (item: EmailQueueItem) => {
        setRetrying(item.id);
        try {
            const updated = await EmailQueueService.retry(item.id);
            setItems(prev => prev.map(i => i.id === updated.id ? updated : i));
            toast.current?.show({ severity: 'success', summary: t('emailQueue.retrySuccess'), life: 3000 });
        } catch (error: any) {
            toast.current?.show({ severity: 'error', summary: t('emailQueue.retryError'), detail: error.message, life: 3000 });
        } finally {
            setRetrying(null);
        }
    };

    const handleRetryAll = async () => {
        setRetryingAll(true);
        try {
            const results = await EmailQueueService.retryAllPending();
            await fetchQueue();
            const sent = results.filter(r => r.status === 'SENT').length;
            toast.current?.show({ severity: 'success', summary: t('emailQueue.retryAllSuccess', { count: sent }), life: 3000 });
        } catch (error: any) {
            toast.current?.show({ severity: 'error', summary: t('emailQueue.retryAllError'), detail: error.message, life: 3000 });
        } finally {
            setRetryingAll(false);
        }
    };

    const statusSeverity = (status: string): 'success' | 'warning' | 'danger' | 'info' => {
        if (status === 'SENT') return 'success';
        if (status === 'PENDING') return 'warning';
        return 'danger';
    };

    const statusBody = (item: EmailQueueItem) => (
        <Tag value={t(`emailQueue.status.${item.status.toLowerCase()}`)} severity={statusSeverity(item.status)} />
    );

    const dateBody = (val: string | undefined) =>
        val ? new Date(val).toLocaleString() : '—';

    const actionsBody = (item: EmailQueueItem) => (
        <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Button
                icon="pi pi-eye"
                rounded
                text
                severity="info"
                tooltip={t('emailQueue.view')}
                tooltipOptions={{ position: 'top' }}
                onClick={() => setViewItem(item)}
            />
            {item.status !== 'SENT' && (
                <Button
                    icon="pi pi-replay"
                    rounded
                    text
                    severity="warning"
                    tooltip={t('emailQueue.retry')}
                    tooltipOptions={{ position: 'top' }}
                    loading={retrying === item.id}
                    onClick={() => handleRetry(item)}
                />
            )}
        </div>
    );

    const hasPending = items.some(i => i.status === 'PENDING' || i.status === 'FAILED');

    if (loading) return <ProgressSpinner />;

    return (
        <>
            <Toast ref={toast} />

            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    label={t('emailQueue.retryAll')}
                    icon="pi pi-refresh"
                    severity="warning"
                    loading={retryingAll}
                    disabled={!hasPending}
                    onClick={handleRetryAll}
                />
            </div>

            <DataTable
                value={items}
                paginator
                rows={20}
                sortField="createdAt"
                sortOrder={-1}
                emptyMessage={t('emailQueue.empty')}
                responsiveLayout="scroll"
            >
                <Column field="to" header={t('emailQueue.to')} sortable />
                <Column field="subject" header={t('emailQueue.subject')} sortable />
                <Column field="status" header={t('emailQueue.statusLabel')} body={statusBody} sortable />
                <Column field="attempts" header={t('emailQueue.attempts')} sortable style={{ width: '5rem' }} />
                <Column
                    field="createdAt"
                    header={t('emailQueue.createdAt')}
                    body={(row: EmailQueueItem) => dateBody(row.createdAt)}
                    sortable
                />
                <Column
                    field="lastAttemptAt"
                    header={t('emailQueue.lastAttemptAt')}
                    body={(row: EmailQueueItem) => dateBody(row.lastAttemptAt)}
                    sortable
                />
                <Column
                    field="sentAt"
                    header={t('emailQueue.sentAt')}
                    body={(row: EmailQueueItem) => dateBody(row.sentAt)}
                    sortable
                />
                <Column field="lastError" header={t('emailQueue.lastError')} />
                <Column header={t('emailQueue.actions')} body={actionsBody} style={{ width: '7rem' }} />
            </DataTable>

            <Dialog
                header={viewItem?.subject}
                visible={viewItem !== null}
                style={{ width: '60vw', maxWidth: '900px' }}
                onHide={() => setViewItem(null)}
                maximizable
            >
                {viewItem && (
                    <div>
                        <p><strong>{t('emailQueue.to')}:</strong> {viewItem.to}</p>
                        <hr />
                        <div dangerouslySetInnerHTML={{ __html: viewItem.html }} />
                    </div>
                )}
            </Dialog>
        </>
    );
};

export default EmailQueueList;
